import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal } from "lucide-react";
import { getAvatarGradient } from "../../../utils/avatarUtils";
import SimpleTooltip from "../SimpleTooltip";
import { getInitials, getUtilColor } from "./tableUtils";

export { getInitials, getUtilColor };

/* ===================== Employee avatar + name line ===================== */

export interface EmployeeCellProps {
  name?: string;
  sub?: string;
  /** Optional override for the gradient (e.g. record.avatarBg) */
  avatarBg?: string;
}

export const EmployeeCell = ({ name, sub, avatarBg }: EmployeeCellProps) => (
  <div className="flex items-center gap-[11px]">
    <div
      className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center text-white font-bold text-[12.5px] flex-none"
      style={{ background: avatarBg || getAvatarGradient(name || "") }}
    >
      {getInitials(name)}
    </div>
    <div className="min-w-0">
      <div className="text-[13.5px] font-bold text-[var(--color-text-primary)] truncate">
        {name || "—"}
      </div>
      {sub ? (
        <div className="text-[11.5px] text-[var(--color-text-tertiary)] truncate">
          {sub}
        </div>
      ) : null}
    </div>
  </div>
);

/* ===================== Status pill with colored dot ===================== */

export interface StatusDotPillProps {
  label: string;
  bg?: string;
  text?: string;
  dot?: string;
}

export const StatusDotPill = ({
  label,
  bg = "#F4F3F9",
  text = "#6B7280",
  dot,
}: StatusDotPillProps) => (
  <span
    className="inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-full text-[11.5px] font-bold whitespace-nowrap"
    style={{ background: bg, color: text }}
  >
    {dot && (
      <span
        className="w-[6px] h-[6px] rounded-full"
        style={{ background: dot }}
      />
    )}
    {label}
  </span>
);

/* ===================== Status pill with icon ===================== */

export interface StatusPillProps {
  label: string;
  bg?: string;
  text?: string;
  icon?: React.ReactNode;
}

export const StatusPill = ({
  label,
  bg = "#F4F3F9",
  text = "#6B7280",
  icon,
}: StatusPillProps) => (
  <span
    className="inline-flex items-center gap-[6px] px-3 py-[5px] rounded-full text-[11.5px] font-bold whitespace-nowrap"
    style={{ background: bg, color: text }}
  >
    {icon}
    {label}
  </span>
);

/* ===================== Utilization / progress bar ===================== */

export interface UtilizationCellProps {
  pct: number;
  color?: string;
}

export const UtilizationCell = ({
  pct,
  color = getUtilColor(pct),
}: UtilizationCellProps) => {
  const width = Math.min(100, Math.max(0, pct));
  return (
    <div className="flex items-center gap-[9px] min-w-[120px]">
      <div className="flex-1 h-[7px] rounded-full bg-[var(--color-bg-track)] overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${width}%`, background: color }}
        />
      </div>
      <span className="text-xs font-bold whitespace-nowrap" style={{ color }}>
        {pct}%
      </span>
    </div>
  );
};

/* ===================== Progress rows (leave-balance breakdown) ===================== */

export interface ProgressRowProps {
  name: string;
  remaining: number;
  total: number;
  color: string;
}

export const ProgressRow = ({
  name,
  remaining,
  total,
  color,
}: ProgressRowProps) => {
  const used = Math.max(0, total - remaining);
  const fillPct = total > 0 ? Math.round((used / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-[7px]">
        <span className="text-[13px] font-bold text-[var(--color-text-primary)]">
          {name}
        </span>
        <span className="text-[12.5px] font-semibold text-[var(--color-text-secondary)]">
          {remaining} of {total} left
        </span>
      </div>
      <div className="h-[7px] rounded-full bg-[var(--color-bg-track)] overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${fillPct}%`, background: color }}
        />
      </div>
    </div>
  );
};

/* ===================== Icon action button ===================== */

export interface IconActionButtonProps {
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}

export const IconActionButton = ({
  title,
  onClick,
  children,
}: IconActionButtonProps) => (
  <button
    type="button"
    {...(title ? { "aria-label": title } : {})}
    onClick={onClick}
    className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-muted)]"
  >
    {children}
  </button>
);

/* ===================== Row actions dropdown menu ===================== */

export interface MenuItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  danger?: boolean;
  onClick?: () => void;
  dividerBefore?: boolean;
}

export interface RowActionsMenuProps {
  items: MenuItem[];
  className?: string;
}

export const RowActionsMenu = ({ items, className = "" }: RowActionsMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <SimpleTooltip label="More" side="top">
        <div>
          <IconActionButton onClick={() => setOpen((o) => !o)}>
            <MoreHorizontal className="w-4 h-4" />
          </IconActionButton>
        </div>
      </SimpleTooltip>
      {open && (
        <div className="absolute top-[38px] right-0 z-30 w-[172px] bg-white border border-[#ECEAF4] rounded-[13px] shadow-[0_16px_40px_-12px_rgba(20,16,40,.28)] p-[6px]">
          {items.map((item) => (
            <div key={item.key}>
              {item.dividerBefore && (
                <div className="h-px bg-[var(--color-border-tertiary)] my-[5px] mx-[6px]" />
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                className={`w-full flex items-center gap-[10px] px-[11px] py-[9px] rounded-[9px] cursor-pointer text-[13px] font-${
                  item.danger ? "700" : "600"
                } ${item.danger ? "text-[#B45309]" : "text-[var(--color-text-strong)]"} ${
                  item.danger
                    ? "hover:bg-[#FFFBEB]"
                    : "hover:bg-[var(--color-bg-muted)]"
                }`}
              >
                {item.icon && <span className="text-[var(--color-text-secondary)]">{item.icon}</span>}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};